/**
 * DOM Context Extraction
 * 
 * Extracts structured information about interactive elements on the page
 * to provide context to the LLM for visual assistance.
 */

import { isSensitiveField } from './sensitive-filter.js';

// Default limits to prevent token explosion
const DEFAULT_LIMITS = {
  buttons: 20,
  links: 30,
  forms: 5,
  fieldsPerForm: 15,
  sections: 10,
  headings: 15,
  maxTextLength: 100,
  maxSummaryLength: 150
};

/**
 * Generate a unique, stable CSS selector for an element
 * Prioritizes stable attributes over auto-generated IDs
 * 
 * @param {HTMLElement} element - The element to generate selector for
 * @returns {string} - CSS selector string
 */
export function generateSelector(element) {
  if (!element || !element.tagName) return null;
  
  // Priority 1: data-testid (designed to be stable)
  if (element.dataset?.testid) {
    return `[data-testid="${element.dataset.testid}"]`;
  }
  
  // Priority 2: data-cy (Cypress test attribute)
  if (element.dataset?.cy) {
    return `[data-cy="${element.dataset.cy}"]`;
  }
  
  // Priority 3: aria-label (accessibility, usually stable)
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel && ariaLabel.length < 50) {
    return `[aria-label="${ariaLabel.replace(/"/g, '\\"')}"]`;
  }
  
  // Priority 4: Stable ID (not auto-generated)
  if (element.id && !isAutoGeneratedId(element.id)) {
    return `#${CSS.escape(element.id)}`;
  }
  
  // Priority 5: name attribute (for form fields)
  if (element.name) {
    const tag = element.tagName.toLowerCase();
    return `${tag}[name="${element.name}"]`;
  }
  
  // Priority 6: Unique text content for buttons/links
  if (['BUTTON', 'A'].includes(element.tagName)) {
    const text = element.innerText?.trim();
    if (text && text.length < 30 && text.length > 0) {
      const tag = element.tagName.toLowerCase();
      // Check if this text is unique on the page
      const matches = document.querySelectorAll(`${tag}`);
      const sameTextCount = Array.from(matches).filter(
        el => el.innerText?.trim() === text
      ).length;
      
      if (sameTextCount === 1) {
        return `${tag}:contains("${text.replace(/"/g, '\\"')}")`;
      }
    }
  }
  
  // Priority 7: Build path from stable ancestors
  return buildPathSelector(element);
}

/**
 * Check if an ID looks auto-generated (React, Angular, Vue, etc.)
 */
function isAutoGeneratedId(id) {
  const autoGenPatterns = [
    /^:r[0-9a-z]+:$/,           // React 18+ useId
    /^react-/i,                  // React
    /^ng-/i,                     // Angular
    /^vue-/i,                    // Vue
    /^ember[0-9]+/i,             // Ember
    /^_[a-z0-9]{8,}$/i,          // Generic hash IDs
    /^[a-f0-9]{8}-[a-f0-9]{4}/i, // UUID-like
    /^uid-/i,                    // Generic UID
    /^id[0-9]+$/i,               // Generic numbered IDs
  ];
  
  return autoGenPatterns.some(pattern => pattern.test(id));
}

/**
 * Build a selector path from ancestors
 */
function buildPathSelector(element, maxDepth = 3) {
  const path = [];
  let current = element;
  let depth = 0;
  
  while (current && current !== document.body && depth < maxDepth) {
    const tag = current.tagName.toLowerCase();
    
    // Try to add distinguishing info
    if (current.id && !isAutoGeneratedId(current.id)) {
      path.unshift(`#${CSS.escape(current.id)}`);
      break; // ID is unique, stop here
    }
    
    // Add class if it seems meaningful
    const meaningfulClass = getMeaningfulClass(current);
    if (meaningfulClass) {
      path.unshift(`${tag}.${meaningfulClass}`);
    } else {
      // Add nth-child if needed
      const siblings = current.parentElement?.children;
      if (siblings && siblings.length > 1) {
        const index = Array.from(siblings).indexOf(current) + 1;
        path.unshift(`${tag}:nth-child(${index})`);
      } else {
        path.unshift(tag);
      }
    }
    
    current = current.parentElement;
    depth++;
  }
  
  return path.join(' > ');
}

/**
 * Get a meaningful class name (not utility classes)
 */
function getMeaningfulClass(element) {
  const classes = Array.from(element.classList || []);
  
  // Skip utility class patterns (Tailwind, Bootstrap, etc.)
  const utilityPatterns = [
    /^(flex|grid|block|inline|hidden)$/,
    /^(m|p|w|h|min|max)-/,          // Spacing/sizing
    /^(text|font|bg|border)-/,      // Typography/colors
    /^(col|row)-/,                  // Grid
    /^(sm|md|lg|xl|2xl):/,          // Responsive
    /^hover:|focus:|active:/,       // States
    /^[a-z]{1,3}-[0-9]+$/,          // Generic utilities
  ];
  
  const meaningfulClasses = classes.filter(cls => 
    !utilityPatterns.some(pattern => pattern.test(cls)) &&
    cls.length > 2 &&
    cls.length < 30
  );
  
  return meaningfulClasses[0] || null;
}

/**
 * Check if element is in or near viewport
 */
function isInViewport(element, buffer = 500) {
  const rect = element.getBoundingClientRect();
  return (
    rect.bottom >= -buffer &&
    rect.top <= window.innerHeight + buffer &&
    rect.right >= 0 &&
    rect.left <= window.innerWidth
  );
}

/**
 * Check if element is visible (not hidden)
 */
function isVisible(element) {
  if (!element) return false;
  
  const style = window.getComputedStyle(element);
  
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.opacity !== '0' &&
    element.offsetWidth > 0 &&
    element.offsetHeight > 0
  );
}

/**
 * Extract buttons from the page
 */
function extractButtons(limits = DEFAULT_LIMITS) {
  const buttons = [];
  const selector = 'button, [type="submit"], [type="button"], [role="button"], .btn, .button';
  const elements = document.querySelectorAll(selector);
  
  for (const el of elements) {
    if (buttons.length >= limits.buttons) break;
    if (!isVisible(el)) continue;
    
    const text = (el.innerText || el.value || el.getAttribute('aria-label') || '').trim();
    if (!text) continue;
    
    buttons.push({
      selector: generateSelector(el),
      text: text.substring(0, limits.maxTextLength),
      type: el.type || 'button',
      disabled: el.disabled || false,
      inViewport: isInViewport(el, 0)
    });
  }
  
  return buttons;
}

/**
 * Extract links from the page
 */
function extractLinks(limits = DEFAULT_LIMITS) {
  const links = [];
  const elements = document.querySelectorAll('a[href]');
  const currentOrigin = window.location.origin;
  
  for (const el of elements) {
    if (links.length >= limits.links) break;
    if (!isVisible(el)) continue;
    
    const text = (el.innerText || el.getAttribute('aria-label') || '').trim();
    if (!text || text.length < 2) continue;
    
    // Skip anchor links and javascript:void
    const href = el.href;
    if (!href || href.startsWith('javascript:') || href === '#') continue;
    
    let isInternal = false;
    try {
      isInternal = new URL(href).origin === currentOrigin;
    } catch {}
    
    links.push({
      selector: generateSelector(el),
      text: text.substring(0, limits.maxTextLength),
      href: href,
      internal: isInternal,
      inViewport: isInViewport(el, 0)
    });
  }
  
  return links;
}

/**
 * Extract forms from the page (with sensitive field filtering)
 */
function extractForms(limits = DEFAULT_LIMITS) {
  const forms = [];
  const elements = document.querySelectorAll('form');
  let sensitiveCount = 0;
  
  for (const form of elements) {
    if (forms.length >= limits.forms) break;
    if (!isVisible(form)) continue;
    
    const fields = [];
    const fieldElements = form.querySelectorAll('input, textarea, select');
    
    for (const field of fieldElements) {
      if (fields.length >= limits.fieldsPerForm) break;
      
      // Skip hidden fields
      if (field.type === 'hidden') continue;
      
      // IMPORTANT: Filter sensitive fields
      if (isSensitiveField(field)) {
        sensitiveCount++;
        continue;
      }
      
      // Get label
      let label = null;
      if (field.id) {
        const labelEl = document.querySelector(`label[for="${field.id}"]`);
        label = labelEl?.innerText?.trim();
      }
      if (!label && field.getAttribute('aria-label')) {
        label = field.getAttribute('aria-label');
      }
      if (!label && field.placeholder) {
        label = field.placeholder;
      }
      
      fields.push({
        selector: generateSelector(field),
        name: field.name || null,
        type: field.type || 'text',
        label: label?.substring(0, 50) || null,
        placeholder: field.placeholder?.substring(0, 50) || null,
        required: field.required || false,
        // NEVER include value for security
      });
    }
    
    if (fields.length === 0) continue;
    
    forms.push({
      selector: generateSelector(form),
      id: form.id || null,
      action: form.action || null,
      method: (form.method || 'get').toUpperCase(),
      fields: fields
    });
  }
  
  return { forms, sensitiveCount };
}

/**
 * Extract sections/landmarks from the page
 */
function extractSections(limits = DEFAULT_LIMITS) {
  const sections = [];
  const selector = 'section, article, main, [role="main"], [role="region"], .section';
  const elements = document.querySelectorAll(selector);
  
  for (const el of elements) {
    if (sections.length >= limits.sections) break;
    if (!isVisible(el)) continue;
    
    // Get a summary of the section content
    const heading = el.querySelector('h1, h2, h3');
    const headingText = heading?.innerText?.trim() || '';
    
    // Get first paragraph or first N characters
    const firstP = el.querySelector('p');
    const summary = headingText || 
                    firstP?.innerText?.trim().substring(0, limits.maxSummaryLength) ||
                    el.innerText?.trim().substring(0, limits.maxSummaryLength) || '';
    
    sections.push({
      selector: generateSelector(el),
      id: el.id || null,
      tagName: el.tagName.toLowerCase(),
      summary: summary.substring(0, limits.maxSummaryLength),
      inViewport: isInViewport(el, 0)
    });
  }
  
  return sections;
}

/**
 * Extract headings from the page
 */
function extractHeadings(limits = DEFAULT_LIMITS) {
  const headings = [];
  const elements = document.querySelectorAll('h1, h2, h3');
  
  for (const el of elements) {
    if (headings.length >= limits.headings) break;
    if (!isVisible(el)) continue;
    
    const text = el.innerText?.trim();
    if (!text) continue;
    
    headings.push({
      selector: generateSelector(el),
      level: el.tagName.toLowerCase(),
      text: text.substring(0, limits.maxTextLength),
      inViewport: isInViewport(el, 0)
    });
  }
  
  return headings;
}

/**
 * Main function: Extract full DOM context
 * 
 * @param {Object} options - Extraction options
 * @param {Object} options.limits - Override default limits
 * @returns {Object} - Structured DOM context
 */
export async function extractDOMContext(options = {}) {
  const limits = { ...DEFAULT_LIMITS, ...options.limits };
  
  // Extract all elements
  const buttons = extractButtons(limits);
  const links = extractLinks(limits);
  const { forms, sensitiveCount } = extractForms(limits);
  const sections = extractSections(limits);
  const headings = extractHeadings(limits);
  
  // Count totals
  const totalElements = buttons.length + links.length + 
    forms.reduce((sum, f) => sum + f.fields.length, 0) + 
    sections.length + headings.length;
  
  const visibleInViewport = 
    buttons.filter(b => b.inViewport).length +
    links.filter(l => l.inViewport).length +
    sections.filter(s => s.inViewport).length +
    headings.filter(h => h.inViewport).length;
  
  // Detect site language
  // Priority: HTML lang attribute > browser language
  const htmlLang = document.documentElement.lang || 
                   document.querySelector('html')?.getAttribute('lang') ||
                   null;
  const browserLang = navigator.language || 
                      (navigator.languages && navigator.languages[0]) || 
                      null;
  const language = htmlLang || browserLang || null;
  
  return {
    url: window.location.href,
    title: document.title,
    timestamp: Date.now(),
    language: language, // Site language (from HTML lang attribute or browser)
    
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      pageHeight: document.documentElement.scrollHeight
    },
    
    buttons,
    links,
    forms,
    sections,
    headings,
    
    metadata: {
      totalElements,
      visibleInViewport,
      formsCount: forms.length,
      sensitiveFieldsFiltered: sensitiveCount
    }
  };
}

/**
 * Extract minimal context (for re-extraction after navigation)
 */
export async function extractMinimalContext() {
  // Detect site language
  const htmlLang = document.documentElement.lang || 
                   document.querySelector('html')?.getAttribute('lang') ||
                   null;
  const browserLang = navigator.language || 
                      (navigator.languages && navigator.languages[0]) || 
                      null;
  const language = htmlLang || browserLang || null;
  
  return {
    url: window.location.href,
    title: document.title,
    timestamp: Date.now(),
    language: language,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollY: window.scrollY
    }
  };
}

export default {
  extractDOMContext,
  extractMinimalContext,
  generateSelector,
  DEFAULT_LIMITS
};
